import { Injectable, InjectionToken, computed, inject, signal } from '@angular/core';
import { bubbleBadge, permissionToPref, unreadCount } from '@afs/chat-core';
import type {
  BubbleBadge, ChatClient, ChatMessage, Conversation, DesktopPref, Heartbeat, Notifier,
  Participant, PresenceEntry,
} from '@afs/chat-core';
import { ChatApiService } from './chat-api.service';
import type { ChatDuty } from './chat-api.service';
import { ChatIdentityService } from './chat-identity.service';
import { CHAT_RUNTIME } from './chat-runtime';
import { AuthService } from './auth.service';
import { HelperContextService } from '../features/helper/helper-context.service';
import { environment } from '../../environments/environment';

/** How long a typing indicator survives with no follow-up. */
const TYPING_MS = 4_000;

/**
 * How often the open conversation is re-read from the database.
 *
 * Realtime is the fast path, not the only path. A CSR sat with a chat open in
 * UAT while typing indicators kept arriving on the broadcast half of the channel
 * and not one message arrived on the postgres_changes half — a second client
 * signed in as the same person received them all, so the socket was up and the
 * binding was simply deaf, with nothing in the channel's status saying so. The
 * helper hit the same thing: their own message reached the customer and never
 * appeared on their own screen. That is why this is a timer, not an error handler.
 */
const RESYNC_MS = 10_000;

/**
 * The chat API's base URL, as a token rather than a direct environment read, so
 * "this environment has chat switched off" is a provider swap in a test instead
 * of a setter on the service's public API.
 */
export const CHAT_API_BASE = new InjectionToken<string>('CHAT_API_BASE', {
  providedIn: 'root',
  factory: () => environment.chatApiUrl,
});

/** Returns the current instant as an ISO string. */
export type ChatClock = () => string;

/**
 * Every instant this service stamps comes from here, never from `new Date()`
 * inside a method: last_read_at decides what the badge counts, and a boundary
 * you cannot move is a boundary you cannot test.
 */
export const CHAT_CLOCK = new InjectionToken<ChatClock>('CHAT_CLOCK', {
  providedIn: 'root',
  factory: () => () => new Date().toISOString(),
});

/**
 * The one piece of chat state in the survey app.
 *
 * It owns the ChatClient, the Notifier and the Heartbeat, and publishes plain
 * signals. Every component below it is a pure input/output component, which is
 * what makes the UI rules testable by passing arrays instead of faking Supabase.
 *
 * Two things it deliberately does not do:
 *   - It never opens chat:queue. Spec section 9 gives survey the online staff
 *     list and its own conversations, nothing else.
 *   - It never writes conversation state. Messages and last_read_at go to
 *     Supabase; start, invite, close, reopen and break go through the API,
 *     because break_until is a service-role column and that is what makes the
 *     last-person-on-duty rule impossible to dodge from a browser.
 */
@Injectable({ providedIn: 'root' })
export class SurveyChatService {
  private readonly api = inject(ChatApiService);
  private readonly identity = inject(ChatIdentityService);
  private readonly runtime = inject(CHAT_RUNTIME);
  private readonly auth = inject(AuthService);
  private readonly helper = inject(HelperContextService);
  private readonly base = inject(CHAT_API_BASE);
  private readonly clock = inject(CHAT_CLOCK);

  private client: ChatClient | null = null;
  private notifier: Notifier | null = null;
  private heartbeat: Heartbeat | null = null;
  private started = false;
  private typingTimer: ReturnType<typeof setTimeout> | null = null;
  private resyncTimer: ReturnType<typeof setInterval> | null = null;

  /**
   * The instant last written to afs_chat_participants.last_read_at, per
   * conversation. Kept because the badge has to count what arrived *since the
   * user last looked*: passing null here would count the whole thread every
   * time, which is the bug the portal shipped. Missing means nothing has been
   * read yet, which is the one case where null is the right answer.
   */
  private lastReadAt: Record<string, string> = {};

  private readonly availableState = signal(false);
  private readonly dutyState = signal<ChatDuty | null>(null);
  private readonly staffState = signal<PresenceEntry[]>([]);
  private readonly conversationsState = signal<Conversation[]>([]);
  private readonly watchingState = signal<Conversation[]>([]);
  private readonly activeIdState = signal<string | null>(null);
  private readonly messagesState = signal<ChatMessage[]>([]);
  private readonly participantsState = signal<Participant[]>([]);
  private readonly unreadState = signal<Record<string, number>>({});
  private readonly panelOpenState = signal(false);
  private readonly busyState = signal(false);
  private readonly errorState = signal<string | null>(null);
  private readonly typingNameState = signal<string | null>(null);
  private readonly soundOnState = signal(true);
  private readonly desktopPermissionState = signal<DesktopPref>('unasked');

  readonly available = this.availableState.asReadonly();
  readonly duty = this.dutyState.asReadonly();
  readonly staff = this.staffState.asReadonly();
  readonly conversations = this.conversationsState.asReadonly();
  readonly watching = this.watchingState.asReadonly();
  readonly activeId = this.activeIdState.asReadonly();
  readonly messages = this.messagesState.asReadonly();
  readonly participants = this.participantsState.asReadonly();
  readonly unread = this.unreadState.asReadonly();
  readonly panelOpen = this.panelOpenState.asReadonly();
  readonly busy = this.busyState.asReadonly();
  readonly error = this.errorState.asReadonly();
  readonly typingName = this.typingNameState.asReadonly();
  readonly soundOn = this.soundOnState.asReadonly();
  readonly desktopPermission = this.desktopPermissionState.asReadonly();

  /**
   * meOnBreak is computed server-side from break_until > now, and an expired
   * break comes back as meOnBreak false with a null break_until. The browser
   * compares no timestamps of its own: the API is the thing enforcing the
   * last-person-on-duty rule, so it is also the thing that decides.
   */
  readonly onBreak = computed(() => this.dutyState()?.meOnBreak === true);

  readonly badge = computed<BubbleBadge>(() => {
    const watched = new Set(this.watchingState().map((c) => c.id));
    const counts = this.unreadState();
    const ownUnread = Object.keys(counts)
      .filter((id) => !watched.has(id))
      .reduce((sum, id) => sum + counts[id], 0);

    // queued is structurally zero here — the survey never opens chat:queue. It
    // still goes through bubbleBadge so the shape matches the other two apps
    // and the rule stays in one place. `isStaff` is true because every survey
    // user is staff (spec section 2, decision 2), and it is the one input here
    // no test can pin: bubbleBadge reads it only to gate `queued`, which is
    // already zero. It is a required field, so it cannot be dropped either.
    return bubbleBadge({ ownUnread, queued: 0, onBreak: this.onBreak(), isStaff: true });
  });

  async init(): Promise<void> {
    if (this.started) return;

    const userId = this.identity.userId();
    // No base means the environment has chat switched off; no user id means
    // nobody is signed in against the data project. Either way there is
    // nothing to show and nothing to ask the API.
    if (this.base === '' || userId === null) return;

    const duty = await this.api.duty();
    if (duty.status !== 'ok') return;   // 503 is the toggle. Stay invisible.

    this.started = true;
    this.dutyState.set(duty.value);

    this.notifier = this.runtime.createNotifier();
    this.notifier.armAudioOnFirstGesture();
    this.soundOnState.set(this.notifier.prefs.sound === 'on');
    this.desktopPermissionState.set(this.notifier.prefs.desktop);

    this.client = this.runtime.createClient(userId);
    this.client.openInbox((conversationId) => void this.onJoined(conversationId));
    this.client.openPresence(this.selfPresence(), (entries) => {
      this.staffState.set(entries.filter((e) => e.userId !== userId));
    });

    this.heartbeat = this.runtime.createHeartbeat({
      sendHeartbeat: () => void this.api.heartbeat(),
      sendOffDutyBeacon: () => this.api.offDutyBeacon(),
    });
    this.heartbeat.start();

    await this.refreshConversations();
    this.resyncTimer = setInterval(() => void this.resync(), RESYNC_MS);
    this.availableState.set(true);
  }

  /**
   * Re-read the open conversation and play back anything realtime never handed
   * over. Public because the timer is the only other caller and a test cannot
   * wind setInterval forward.
   *
   * Every row goes through accept(), so a message that did arrive is dropped on
   * its id and one that did not gets the badge and the alert it would have had.
   * That makes a deaf socket cost up to RESYNC_MS, not the chat.
   */
  async resync(): Promise<void> {
    const conversationId = this.activeIdState();
    if (conversationId === null || !this.started) return;

    for (const message of await this.runtime.loadMessages(conversationId)) {
      this.accept(conversationId, message);
    }
  }

  async dispose(): Promise<void> {
    if (this.typingTimer !== null) clearTimeout(this.typingTimer);
    this.typingTimer = null;

    if (this.resyncTimer !== null) clearInterval(this.resyncTimer);
    this.resyncTimer = null;

    this.heartbeat?.dispose();
    this.notifier?.dispose();
    await this.client?.closePresence();
    await this.client?.disposeAll();

    this.heartbeat = null;
    this.notifier = null;
    this.client = null;
    this.started = false;
    this.availableState.set(false);
  }

  async openPanel(): Promise<void> {
    this.panelOpenState.set(true);
    this.notifier?.clearTitleFlash();
    // A tablet sleeps in a van for an hour. Re-read rather than trust that
    // every postgres_changes event survived the nap.
    await this.refreshConversations();
  }

  closePanel(): void {
    this.panelOpenState.set(false);
  }

  async select(conversationId: string | null): Promise<void> {
    const previous = this.activeIdState();
    if (previous === conversationId) return;
    if (previous !== null) await this.client?.closeConversation(previous);

    this.activeIdState.set(conversationId);
    this.typingNameState.set(null);
    this.messagesState.set([]);
    this.participantsState.set([]);

    if (conversationId === null) return;

    this.client?.openConversation(conversationId, {
      onMessage: (m) => this.accept(conversationId, m),
      onTyping: (p) => this.showTyping(p),
    });

    this.messagesState.set(await this.runtime.loadMessages(conversationId));
    this.participantsState.set(await this.runtime.loadParticipants(conversationId));

    this.clearUnread(conversationId);
    await this.markRead(conversationId);
  }

  async send(body: string): Promise<void> {
    const conversationId = this.activeIdState();
    const trimmed = body.trim();
    if (conversationId === null || trimmed.length === 0) return;

    this.busyState.set(true);
    this.errorState.set(null);
    try {
      await this.client?.sendMessage(conversationId, trimmed);
    } catch {
      // Losing a paragraph typed one-handed on a roof is the failure nobody
      // forgives. Say so, so the composer can hand the text back.
      this.errorState.set('that message was not sent — try again');
    } finally {
      this.busyState.set(false);
    }
  }

  async notifyTyping(): Promise<void> {
    const conversationId = this.activeIdState();
    if (conversationId === null) return;
    await this.client?.sendTyping(conversationId, this.chatName());
  }

  /**
   * `targetUserId` null means "ask customer service": the conversation goes
   * into the pool and whoever is free claims it.
   *
   * A named person is passed to the server, which creates the row already
   * claimed by them. It used to be a second call — start, then invite — which
   * left the conversation queued in between, and the queue channel carries
   * every INSERT: in UAT a question for one CSR rang the other CSR's bubble as
   * "New chat waiting", and they could still claim it out from under the person
   * who was asked.
   */
  async startWith(targetUserId: string | null, firstMessage: string): Promise<void> {
    this.busyState.set(true);
    this.errorState.set(null);
    try {
      const started = await this.api.start(firstMessage, targetUserId);
      if (started.status !== 'ok' || started.value === null) {
        this.errorState.set(started.message ?? 'that chat could not be started');
        return;
      }

      await this.refreshConversations();
      await this.select(started.value.id);
    } finally {
      this.busyState.set(false);
    }
  }

  async closeChat(): Promise<void> {
    const conversationId = this.activeIdState();
    if (conversationId === null) return;

    const result = await this.api.close(conversationId);
    if (result.status !== 'ok') {
      this.errorState.set(result.message ?? 'that chat could not be closed');
      return;
    }
    await this.refreshConversations();
  }

  async reopen(): Promise<void> {
    const conversationId = this.activeIdState();
    if (conversationId === null) return;

    const result = await this.api.reopen(conversationId);
    if (result.status !== 'ok') {
      // A chat closed on an earlier day cannot be reopened; a new one is the
      // answer, and the API's wording says so better than a guess would.
      this.errorState.set(result.message ?? 'that chat could not be reopened');
      return;
    }
    await this.refreshConversations();
  }

  async startBreak(minutes: number, reason: string | null): Promise<void> {
    // error() is one signal, shared with send, startWith, closeChat and reopen,
    // and the panel reads it straight after this call to decide what the break
    // control refused. Clearing it first — as send and startWith already do —
    // is what stops "that message was not sent" being printed inside the break
    // control as though the API had refused the break.
    this.errorState.set(null);

    const result = await this.api.startBreak(minutes, reason);
    if (result.status !== 'ok') {
      this.errorState.set(result.message ?? 'that break was refused');
      return;
    }
    await this.refreshDuty();
  }

  async endBreak(): Promise<void> {
    // Same shared signal, same reason as startBreak.
    this.errorState.set(null);

    const result = await this.api.endBreak();
    if (result.status !== 'ok') {
      this.errorState.set(result.message ?? 'that break could not be ended');
      return;
    }
    await this.refreshDuty();
  }

  async askDesktopPermission(): Promise<void> {
    if (this.desktopPermissionState() !== 'unasked') return;
    const permission = await this.notifier!.requestDesktopPermission();
    this.desktopPermissionState.set(permissionToPref(permission));
  }

  setSound(on: boolean): void {
    this.notifier?.setSoundPref(on ? 'on' : 'off');
    this.soundOnState.set(on);
  }

  // --- internals ---------------------------------------------------------

  /**
   * The name this person goes by in chat.
   *
   * Field helpers share one login, so the account name is the same for all of them.
   * The name they picked on the way in is the only thing that tells two of them
   * apart, and it has to be the same name everywhere: the API already writes it onto
   * the conversation, and in UAT the roster still read "on duty" under the shared
   * account while the tab beside it read the picked name.
   */
  private chatName(): string {
    return this.helper.helper()?.name ?? this.auth.displayName() ?? 'Someone';
  }

  private selfPresence(): PresenceEntry {
    return {
      userId: this.identity.userId()!,
      displayName: this.chatName(),
      role: this.auth.role() ?? 'Helper',
      breakUntil: this.dutyState()?.breakUntil ?? null,
      idle: false,
    };
  }

  /**
   * Re-read duty from the API and republish this person's presence.
   *
   * Public because nothing else can end a break on screen. The countdown is a
   * computed over `duty()` and `Date.now()` is not a signal, so a panel opened
   * during a break would read "back in 15 min" forever while the server has
   * long since decided `meOnBreak: false`. The bubble polls this; the browser
   * still decides nothing itself.
   */
  async refreshDuty(): Promise<void> {
    const duty = await this.api.duty();
    if (duty.status !== 'ok') return;

    this.dutyState.set(duty.value);
    // Other people's panels draw the break from this, so republish rather than
    // wait for the next heartbeat.
    await this.client?.updatePresence(this.selfPresence());
  }

  /**
   * Somebody put this helper into a conversation.
   *
   * An invite and a transfer both write afs_chat_participants and leave
   * afs_chat_conversations alone, and this app opens no queue channel at all,
   * so nothing else in it can hear either. Before this, a chat handed to a
   * helper was silent: no sound, no badge, and it appeared only when they
   * happened to open the panel, which is the one place that re-reads the list.
   *
   * A conversation that arrives as `watching` gets no alert — spec section 9,
   * watching is the transcript, not the responsibility.
   */
  private async onJoined(conversationId: string): Promise<void> {
    const known = [...this.conversationsState(), ...this.watchingState()].some(
      (c) => c.id === conversationId,
    );
    if (known) return;

    await this.refreshConversations();

    const joined = this.conversationsState().find((c) => c.id === conversationId);
    if (joined === undefined) return;

    this.notifier?.notify({
      reason: 'transfer',
      title: 'A chat was handed to you',
      body: joined.subject ?? 'A customer is waiting on you',
      conversationId,
      onBreak: this.onBreak(),
      watching: false,
      idle: this.heartbeat?.idle ?? false,
    });

    // With the panel open, put it on screen — select() marks it read, which is
    // right because the helper is looking at it. With the panel shut, reading
    // it is exactly wrong: the CRM did that and the bubble carried no badge at
    // all, so the CSR heard the sound and had nothing to click.
    if (this.panelOpenState()) {
      if (this.activeIdState() === null) await this.select(conversationId);
      return;
    }

    const messages = await this.runtime.loadMessages(conversationId);
    const counted = unreadCount(
      messages, this.lastReadAt[conversationId] ?? null, this.identity.userId() ?? '',
    );
    if (counted === 0) return;

    this.unreadState.update((counts) => ({ ...counts, [conversationId]: counted }));
  }

  private async refreshConversations(): Promise<void> {
    const userId = this.identity.userId();
    if (userId === null) return;

    const before = this.activeIdState();

    // Closed chats are dropped, exactly as the CRM drops them. afs_chat_participants
    // keeps a row per conversation this person was ever in and never deletes one, so
    // without this the list grows by one per customer, for ever.
    const rows = (await this.runtime.loadMyConversations(userId)).filter(
      (r) => r.conversation.status !== 'closed',
    );

    this.conversationsState.set(rows.filter((r) => !r.watching).map((r) => r.conversation));
    this.watchingState.set(rows.filter((r) => r.watching).map((r) => r.conversation));

    // The chat on screen may be the one that just went. Clearing it here rather
    // than at the call site means every path into this method heals the screen.
    //
    // `before` is read ahead of the await: two of these can be in flight at once
    // — a system line starts one and onJoined starts another — and the slower
    // one would otherwise close a channel that the faster one had just opened,
    // leaving a chat that can send and never receive.
    const activeId = this.activeIdState();
    if (activeId === null || activeId !== before) return;
    if (rows.some((r) => r.conversation.id === activeId)) return;

    await this.select(null);
    this.clearUnread(activeId);
  }

  /** One instant for the row and for the local copy, so the badge and
   *  afs_chat_participants.last_read_at can never disagree. */
  private async markRead(conversationId: string): Promise<void> {
    const at = this.clock();
    this.lastReadAt[conversationId] = at;
    await this.client?.markRead(conversationId, at);
  }

  private accept(conversationId: string, message: ChatMessage): void {
    if (conversationId === this.activeIdState()) {
      // The optimistic local echo and the postgres_changes row are the same
      // message. Rendering it twice is the classic chat bug.
      if (this.messagesState().some((m) => m.id === message.id)) return;
      this.messagesState.update((list) => [...list, message]);
    }

    // A close, a transfer and an invite all write a system line and change
    // nothing else this app can hear — it opens no queue channel. Re-reading the
    // list is how a chat somebody else closed leaves this helper's screen
    // instead of sitting there with a live message box under the closing notice.
    if (message.kind === 'system') void this.refreshConversations();

    // chat-core owns all three exclusions in one place: a system line, this
    // person's own message, and anything stamped at or before what they have
    // already read. The last one is why the instant is tracked rather than
    // passed as null.
    const counted = unreadCount(
      [message], this.lastReadAt[conversationId] ?? null, this.identity.userId() ?? '',
    );
    if (counted === 0) return;

    const onScreen = conversationId === this.activeIdState() && this.panelOpenState();
    if (onScreen) {
      void this.markRead(conversationId);
      return;
    }

    this.unreadState.update((counts) => ({
      ...counts,
      [conversationId]: (counts[conversationId] ?? 0) + counted,
    }));

    // Whether this actually makes a sound is chat-core's decision. This
    // service's only job is to report the facts honestly — including that a
    // watched conversation is watched, which is what silences it.
    this.notifier?.notify({
      reason: 'message',
      title: 'New message',
      body: message.body,
      conversationId,
      onBreak: this.onBreak(),
      watching: this.watchingState().some((c) => c.id === conversationId),
      idle: this.heartbeat?.idle ?? false,
    });
  }

  private clearUnread(conversationId: string): void {
    this.unreadState.update((counts) => {
      const next = { ...counts };
      delete next[conversationId];
      return next;
    });
  }

  private showTyping(payload: { userId: string; displayName: string }): void {
    if (payload.userId === this.identity.userId()) return;

    this.typingNameState.set(payload.displayName);
    if (this.typingTimer !== null) clearTimeout(this.typingTimer);
    this.typingTimer = setTimeout(() => this.typingNameState.set(null), TYPING_MS);
  }
}
